/**
 * CampaignsPage.jsx — Safe Zap Fase 6
 * WhatsApp mass campaigns: create, segment, launch, track progress.
 */
import React, { useEffect, useState } from "react";
import { Icon } from "../shared/components";
import { formatTime, formatMoney } from "../shared/utils";

// ── Helpers ───────────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    rascunho: { label: "Rascunho", cls: "badgeGray" },
    agendada: { label: "Agendada", cls: "badgeBlue" },
    enviando: { label: "Enviando…", cls: "badgeYellow" },
    concluida: { label: "Concluída", cls: "badgeGreen" },
    pausada: { label: "Pausada", cls: "badgeOrange" },
    cancelada: { label: "Cancelada", cls: "badgeRed" },
  };
  const m = map[status] || { label: status, cls: "badgeGray" };
  return <span className={`pill ${m.cls}`}>{m.label}</span>;
}

function ProgressBar({ sent, total }) {
  const pct = total > 0 ? Math.round((sent / total) * 100) : 0;
  return (
    <div className="campaignProgressBar">
      <div className="campaignProgressFill" style={{ width: `${pct}%` }} />
      <span>{pct}% ({sent}/{total})</span>
    </div>
  );
}

// ── CampaignsPage ─────────────────────────────────────────────────────────────
function CampaignsPage({ api, user }) {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("list"); // list | new
  const [selected, setSelected] = useState(null);
  const [notice, setNotice] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [launching, setLaunching] = useState(false);

  // New campaign form
  const [form, setForm] = useState({
    name: "",
    description: "",
    template_id: "",
    customMessage: "",
    tags: "",
    search: "",
    exclude_recent_days: "",
    scheduled_at: "",
  });

  async function load() {
    setLoading(true);
    try {
      const [campRes, tplRes] = await Promise.all([
        api.get("/campaigns"),
        api.get("/whatsapp-templates"),
      ]);
      setCampaigns(campRes.data || []);
      setTemplates(tplRes.data || []);
    } catch {}
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  // Auto-refresh while any campaign is "enviando"
  useEffect(() => {
    const hasSending = campaigns.some((c) => c.status === "enviando");
    if (!hasSending) return;
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [campaigns]);

  function buildFilters() {
    const f = {};
    if (form.tags.trim()) f.tags = form.tags.split(",").map((t) => t.trim()).filter(Boolean);
    if (form.search.trim()) f.search = form.search.trim();
    if (form.exclude_recent_days) f.exclude_recent_days = Number(form.exclude_recent_days);
    return f;
  }

  async function previewSegment() {
    setPreviewing(true);
    setPreviewData(null);
    try {
      const res = await api.post("/campaigns/preview-segment", { segment_filters: buildFilters() });
      setPreviewData(res.data);
    } catch (err) {
      setNotice(err.response?.data?.error || "Erro ao pré-visualizar segmento.");
    } finally { setPreviewing(false); }
  }

  async function createCampaign(e) {
    e.preventDefault();
    setNotice("");
    try {
      const payload = {
        name: form.name,
        description: form.description || undefined,
        template_id: form.template_id ? Number(form.template_id) : undefined,
        segment_filters: buildFilters(),
        scheduled_at: form.scheduled_at || undefined,
      };
      await api.post("/campaigns", payload);
      setNotice("Campanha criada com sucesso.");
      setForm({ name: "", description: "", template_id: "", customMessage: "", tags: "", search: "", exclude_recent_days: "", scheduled_at: "" });
      setPreviewData(null);
      await load();
      setTab("list");
    } catch (err) {
      setNotice(err.response?.data?.error || "Erro ao criar campanha.");
    }
  }

  async function launch(campaign) {
    if (!window.confirm(`Iniciar envio para ${campaign.total_contacts || "todos os"} contatos da campanha "${campaign.name}"?`)) return;
    setLaunching(campaign.id);
    try {
      const payload = {};
      if (form.customMessage && !campaign.template_id) payload.message = form.customMessage;
      await api.post(`/campaigns/${campaign.id}/launch`, payload);
      setNotice("Campanha iniciada! Envios em andamento.");
      await load();
    } catch (err) {
      setNotice(err.response?.data?.error || "Erro ao iniciar campanha.");
    } finally { setLaunching(null); }
  }

  async function deleteCampaign(campaign) {
    if (!window.confirm(`Excluir campanha "${campaign.name}"?`)) return;
    try {
      await api.delete(`/campaigns/${campaign.id}`);
      if (selected?.id === campaign.id) setSelected(null);
      await load();
    } catch (err) {
      setNotice(err.response?.data?.error || "Erro ao excluir.");
    }
  }

  const selectedCampaign = selected ? campaigns.find((c) => c.id === selected) : null;

  return (
    <div className="campaignsPage">
      <div className="campaignsHeader">
        <div>
          <h2>Campanhas WhatsApp</h2>
          <p>Envie mensagens em massa para segmentos de contatos com rastreamento de entrega.</p>
        </div>
        <button onClick={() => setTab(tab === "new" ? "list" : "new")}>
          <Icon name="plus" size={14} /> {tab === "new" ? "Ver campanhas" : "Nova campanha"}
        </button>
      </div>

      {notice && <div className="noticeBox">{notice}</div>}

      {/* Summary strip */}
      <div className="campaignSummaryStrip">
        <div className="campaignKpi">
          <span>Total</span>
          <strong>{campaigns.length}</strong>
        </div>
        <div className="campaignKpi">
          <span>Ativas</span>
          <strong>{campaigns.filter((c) => c.status === "enviando").length}</strong>
        </div>
        <div className="campaignKpi">
          <span>Concluídas</span>
          <strong>{campaigns.filter((c) => c.status === "concluida").length}</strong>
        </div>
        <div className="campaignKpi">
          <span>Total enviados</span>
          <strong>{campaigns.reduce((s, c) => s + Number(c.sent_count || 0), 0).toLocaleString()}</strong>
        </div>
      </div>

      {tab === "new" && (
        <div className="campaignCreateCard">
          <h3>Nova campanha</h3>
          <form onSubmit={createCampaign} className="campaignForm">
            <div className="campaignFormSection">
              <strong>Identificação</strong>
              <div className="formRow">
                <label>Nome da campanha *</label>
                <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ex: Promoção Maio 2025" required />
              </div>
              <div className="formRow">
                <label>Descrição</label>
                <input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Objetivo desta campanha" />
              </div>
            </div>

            <div className="campaignFormSection">
              <strong>Mensagem</strong>
              <div className="formRow">
                <label>Template aprovado</label>
                <select value={form.template_id} onChange={(e) => setForm((f) => ({ ...f, template_id: e.target.value }))}>
                  <option value="">Nenhum (usar mensagem personalizada)</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              {!form.template_id && (
                <div className="formRow">
                  <label>Mensagem personalizada</label>
                  <textarea rows={4} value={form.customMessage} onChange={(e) => setForm((f) => ({ ...f, customMessage: e.target.value }))} placeholder={"Olá {nome}, tudo bem?\n\nTenho uma oferta especial para você..."} />
                  <small>Use <code>{"{nome}"}</code> para o nome do contato.</small>
                </div>
              )}
            </div>

            <div className="campaignFormSection">
              <strong>Segmentação</strong>
              <div className="formRow2">
                <div>
                  <label>Tags dos contatos</label>
                  <input value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} placeholder="cliente, vip, inativo (separadas por vírgula)" />
                </div>
                <div>
                  <label>Busca por nome/telefone</label>
                  <input value={form.search} onChange={(e) => setForm((f) => ({ ...f, search: e.target.value }))} placeholder="Buscar contatos..." />
                </div>
              </div>
              <div className="formRow">
                <label>Excluir contatos com interação recente (dias)</label>
                <input type="number" min="1" max="365" value={form.exclude_recent_days} onChange={(e) => setForm((f) => ({ ...f, exclude_recent_days: e.target.value }))} placeholder="Ex: 7 (exclui quem foi atendido nos últimos 7 dias)" />
              </div>
              <button type="button" className="secondaryBtn" onClick={previewSegment} disabled={previewing}>
                {previewing ? "Calculando…" : "Pré-visualizar segmento"}
              </button>
              {previewData && (
                <div className="segmentPreview">
                  <div className="segmentPreviewKpis">
                    <span><strong>{previewData.total}</strong> contatos no segmento</span>
                    <span>Custo estimado: <strong>{formatMoney(previewData.estimated_cost || 0)}</strong></span>
                  </div>
                  {previewData.contacts?.length > 0 && (
                    <div className="segmentPreviewList">
                      {previewData.contacts.slice(0, 10).map((c) => (
                        <span key={c.id} className="segmentContactChip">{c.name || c.phone}</span>
                      ))}
                      {previewData.total > 10 && <span className="segmentContactChip muted">+{previewData.total - 10} mais</span>}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="campaignFormSection">
              <strong>Agendamento (opcional)</strong>
              <div className="formRow">
                <label>Data e hora de envio</label>
                <input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))} />
                <small>Deixe em branco para criar como rascunho e lançar manualmente.</small>
              </div>
            </div>

            <div className="formActions">
              <button type="submit">Criar campanha</button>
              <button type="button" className="ghostButton" onClick={() => setTab("list")}>Cancelar</button>
            </div>
          </form>
        </div>
      )}

      {tab === "list" && (
        <div className="campaignListArea">
          {loading && <div className="loadingBox">Carregando campanhas…</div>}
          {!loading && campaigns.length === 0 && (
            <div className="emptyCampaigns">
              <Icon name="send" size={32} />
              <p>Nenhuma campanha criada ainda.</p>
              <button onClick={() => setTab("new")}>Criar primeira campanha</button>
            </div>
          )}
          <div className="campaignGrid">
            {campaigns.map((campaign) => (
              <div
                key={campaign.id}
                className={`campaignCard ${selected === campaign.id ? "selected" : ""}`}
                onClick={() => setSelected(selected === campaign.id ? null : campaign.id)}
              >
                <div className="campaignCardHeader">
                  <div>
                    <strong>{campaign.name}</strong>
                    {campaign.description && <p>{campaign.description}</p>}
                  </div>
                  <StatusBadge status={campaign.status} />
                </div>

                <div className="campaignCardMeta">
                  <span><Icon name="people" size={12} /> {campaign.total_contacts || 0} contatos</span>
                  <span><Icon name="money" size={12} /> {formatMoney(campaign.estimated_cost || 0)}</span>
                  <span><Icon name="clock" size={12} /> {formatTime(campaign.created_at)}</span>
                </div>

                {campaign.status === "enviando" && (
                  <ProgressBar sent={Number(campaign.sent_count || 0)} total={Number(campaign.total_contacts || 1)} />
                )}

                {campaign.status === "concluida" && (
                  <div className="campaignCardStats">
                    <span className="statGreen">✓ {campaign.sent_count || 0} enviados</span>
                    {Number(campaign.failed_count || 0) > 0 && (
                      <span className="statRed">✗ {campaign.failed_count} falhas</span>
                    )}
                    <span className="statGray">Concluída {formatTime(campaign.completed_at)}</span>
                  </div>
                )}

                {/* Expanded detail */}
                {selected === campaign.id && (
                  <div className="campaignCardDetail" onClick={(e) => e.stopPropagation()}>
                    <div className="campaignDetailActions">
                      {["rascunho", "pausada"].includes(campaign.status) && (
                        <button
                          onClick={() => launch(campaign)}
                          disabled={launching === campaign.id}
                          className="accentBtn"
                        >
                          <Icon name="send" size={14} />
                          {launching === campaign.id ? "Iniciando…" : "Lançar campanha"}
                        </button>
                      )}
                      {!["enviando", "concluida"].includes(campaign.status) && (
                        <button className="dangerBtn" onClick={() => deleteCampaign(campaign)}>
                          <Icon name="trash" size={14} /> Excluir
                        </button>
                      )}
                    </div>
                    {campaign.status === "enviando" && (
                      <p className="campaignSendingNote">
                        Enviando mensagens… A página atualiza automaticamente a cada 5 segundos.
                      </p>
                    )}
                    {campaign.started_at && <small>Iniciada: {formatTime(campaign.started_at)}</small>}
                    {campaign.completed_at && <small>Concluída: {formatTime(campaign.completed_at)}</small>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default CampaignsPage;
